import React from 'react';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { StatsigProvider, useClientAsyncInit, useStatsigClient } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

const EventsProvider = ({ children }: React.PropsWithChildren<object>): JSX.Element => {
    const { client } = useClientAsyncInit(
        'client-N3XyS44TtBhO13PIIh7bAyzyRlgBGxeRFSjapTawQiJ',
        { userID: 'a-user' },
        { plugins: [new StatsigAutoCapturePlugin()] },
    );
    return (
        <PostHogProvider
            apiKey="phc_vKtZGbccj0B6BTDB4wqtIJ19l87De4wAB43MvXJpoj7"
            options={{
                api_host: 'https://us.i.posthog.com',
                defaults: '2025-05-24',
            }}
        >
            <StatsigProvider client={client} loadingComponent={children}>
                {children}
            </StatsigProvider>
        </PostHogProvider>
    );
};

type CaptureGoal = (eventName: string, eventData: Record<string, string>) => void;

export const useGoal = (): CaptureGoal => {
    const { client } = useStatsigClient();
    const posthog = usePostHog();

    return React.useCallback((eventName, eventData) => {
        client?.logEvent(eventName, 'captured', eventData);
        posthog?.capture(eventName, eventData);
    }, [client, posthog]);
};

export enum FeatureFlag {
    Control = 'control',
    Variant = 'variant',
    WaitingForBucketing = 'waiting',
}

type UseFlag = {
    flag: FeatureFlag;
    wasIgnored: boolean;
    debug?: {
        posthog: { ready: boolean; value: string | boolean | null | undefined };
        statsig: { ready: boolean; gate?: boolean; error?: string };
    };
};

const interpretPostHog = (value: string | boolean | null | undefined): FeatureFlag | 'waiting' => {
    if (value === undefined || value === null) return 'waiting';
    if (typeof value === 'boolean') return value ? FeatureFlag.Variant : FeatureFlag.Control;
    return value.toLowerCase() === 'variant' ? FeatureFlag.Variant : FeatureFlag.Control;
};

const getStatsigGate = (
    client: ReturnType<typeof useStatsigClient>['client'],
    name: string,
): { ready: boolean; gate?: boolean; error?: string } => {
    try {
        const details = client?.loadingStatus;
        const ready = details === 'Ready';
        if (!ready) return { ready: false };
        const on = client?.checkGate?.(name);
        return { ready: true, gate: on };
    } catch (e) {
        // eslint-disable-next-line
        return { ready: true, gate: undefined, error: String((e as any)?.message) };
    }
};

const IGNORE_PREFIX = 'flag.ignore.';

export const useFlag = (flagName: string): UseFlag => {
    const { client } = useStatsigClient();
    const posthog = usePostHog();
    const ignoreKey = `${IGNORE_PREFIX}${flagName}`;
    const [ignored, setIgnored] = React.useState<boolean>(false);

    const [phReady, setPhReady] = React.useState(false);
    const [phValue, setPhValue] = React.useState<string | boolean | null | undefined>(undefined);
    const phSubscribed = React.useRef(false);

    React.useEffect(() => {
        try {
            const wasIgnored = localStorage.getItem(ignoreKey);
            if (wasIgnored) {
                setIgnored(true);
                return;
            }
            // eslint-disable-next-line no-empty
        } catch (e) {
        }

        if (!posthog) {
            setPhReady(false);
            setPhValue(undefined);
            return;
        }

        try {
            setPhValue(posthog.getFeatureFlag?.(flagName));
        } catch {
            setPhValue(undefined);
        }

        if (!phSubscribed.current && posthog.onFeatureFlags) {
            phSubscribed.current = true;
            posthog.onFeatureFlags(() => {
                try {
                    setPhValue(posthog.getFeatureFlag?.(flagName));
                    setPhReady(true);
                } catch {
                    setPhValue(undefined);
                    setPhReady(true);
                }
            });
        }
    }, [posthog, flagName, ignoreKey]);

    const statsig = React.useMemo(
        () => getStatsigGate(client, flagName),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [client.loadingStatus, flagName],
    );

    const phInterp = interpretPostHog(phValue);
    const posthogFlag = (() => {
        if (ignored) return FeatureFlag.Control;
        if (phInterp === 'waiting' || !phReady) {
            return FeatureFlag.WaitingForBucketing;
        }
        return phInterp;
    })();

    const statsigFlag: FeatureFlag = (() => {
        if (ignored) return FeatureFlag.Control;
        if (!statsig.ready) return FeatureFlag.WaitingForBucketing;
        if (typeof statsig.gate === 'boolean') {
            return statsig.gate ? FeatureFlag.Variant : FeatureFlag.Control;
        }
        return FeatureFlag.Control;
    })();

    React.useEffect(() => {
        if (ignored) return undefined;

        const stillWaiting = posthogFlag === FeatureFlag.WaitingForBucketing
        && statsigFlag === FeatureFlag.WaitingForBucketing;

        if (!stillWaiting) return undefined;

        const id = setTimeout(() => {
            const stillWaitingNow = posthogFlag === FeatureFlag.WaitingForBucketing
                && statsigFlag === FeatureFlag.WaitingForBucketing;

            if (stillWaitingNow) {
                try {
                    localStorage.setItem(ignoreKey, '1');
                    // eslint-disable-next-line no-empty
                } catch (e) {
                }
                setIgnored(true);
            }
        }, 6000);

        return () => clearTimeout(id);
    }, [posthogFlag, statsigFlag, ignored, ignoreKey]);

    if (ignored) {
        return {
            flag: FeatureFlag.Control,
            wasIgnored: ignored,
            debug: {
                posthog: { ready: phReady, value: phValue },
                statsig: { ready: statsig.ready, gate: statsig.gate, error: statsig.error },
            },
        };
    }

    if (posthogFlag === FeatureFlag.WaitingForBucketing && statsigFlag === FeatureFlag.WaitingForBucketing) {
        return {
            flag: FeatureFlag.WaitingForBucketing,
            wasIgnored: ignored,
            debug: {
                posthog: { ready: phReady, value: phValue },
                statsig: { ready: statsig.ready, gate: statsig.gate, error: statsig.error },
            },
        };
    }

    const anyVariant = posthogFlag === FeatureFlag.Variant || statsigFlag === FeatureFlag.Variant;
    const anyControl = posthogFlag === FeatureFlag.Control || statsigFlag === FeatureFlag.Control;

    const combined = anyVariant && !anyControl
        ? FeatureFlag.Variant
        : FeatureFlag.Control;

    return {
        flag: combined,
        wasIgnored: ignored,
        debug: {
            posthog: { ready: phReady, value: phValue },
            statsig: { ready: statsig.ready, gate: statsig.gate, error: statsig.error },
        },
    };
};

export default EventsProvider;
